/**
 * Pure helpers for the building "scan timeline" surface — a single building
 * (location) whose floors (spaces) each carry many dated scans (deliverables)
 * captured over months/years. Powers the `building_floor_timeline` block's
 * three modes: dated list, date scrubber, and before/after compare.
 *
 * Dependency-light (no server imports) so the renderer's client bundle + the
 * unit tests can both import it.
 */

/** One dated capture of a floor (a `matterport_scan` deliverable). */
export type BuildingScan = {
  deliverableId: string;
  /** Public Launch share token (opens the tour); null if not yet minted. */
  token: string | null;
  /** Matterport model id; null when not yet synced (tour renders empty). */
  modelId: string | null;
  /** ISO capture date (yyyy-mm-dd) from matterport_details.provider_captured_at;
   *  null when unknown (sorts to the end). */
  capturedAt: string | null;
  /** Provider state ("Archived" etc.) for badge treatment. */
  status: string | null;
  /** Matterport thumbnail / hero image url for the floor-card preview. */
  thumbnailUrl: string | null;
};

/** One floor of the building (a `spaces` row) + its scans over time. */
export type BuildingFloor = {
  spaceId: string;
  floorLabel: string;
  sortKey: number;
  /** Chronological, oldest -> newest. */
  scans: BuildingScan[];
};

/** Raw row the resolver hands in (one per deliverable). */
export type BuildingScanRow = {
  spaceId: string;
  floorLabel: string | null;
  deliverableId: string;
  token: string | null;
  modelId: string | null;
  capturedAt: string | null;
  status: string | null;
  thumbnailUrl?: string | null;
};

/**
 * Floor ordering: basements (deepest first) -> ground/lobby -> mezzanine ->
 * numbered floors ascending -> roof/penthouse -> descriptive spaces last.
 * Mirrors the import's analyze step so the DB + UI agree on floor order.
 */
export function floorSortKey(label: string | null | undefined): number {
  if (!label) return 100000;
  const lc = label.trim().toLowerCase();
  // Basement-like: "cellar"/"basement" anywhere ("Entire Cellar"), or a
  // leading sub/lower. Deeper (higher number) sorts first (more negative).
  if (/\b(sub[\s-]?cellar|cellar|basement|sub[\s-]?basement)\b/.test(lc) || /^(sub|lower)\b/.test(lc)) {
    const m = lc.match(/(\d+)/);
    return m ? -100 - parseInt(m[1], 10) : -100;
  }
  if (/^(ground|lobby|main|g$)/.test(lc)) return 0;
  if (/^mezz/.test(lc)) return 0.5;
  if (/^(roof|rooftop|penthouse|ph)/.test(lc)) return 9000;
  const m = lc.match(/(\d+)/);
  if (m) return parseInt(m[1], 10);
  return 50000;
}

/** Chronological sort (oldest first); null dates sink to the end, stable. */
function byCapturedAtAsc(a: BuildingScan, b: BuildingScan): number {
  if (a.capturedAt && b.capturedAt) return a.capturedAt.localeCompare(b.capturedAt);
  if (a.capturedAt) return -1;
  if (b.capturedAt) return 1;
  return 0;
}

/**
 * Group per-deliverable rows into floors, each floor's scans sorted
 * chronologically, floors sorted by `floorSortKey`. Deduplicates a scan
 * that somehow appears twice (same deliverableId) defensively.
 */
export function groupFloors(rows: readonly BuildingScanRow[]): BuildingFloor[] {
  const bySpace = new Map<string, BuildingFloor>();
  const seen = new Set<string>();
  for (const r of rows) {
    if (seen.has(r.deliverableId)) continue;
    seen.add(r.deliverableId);
    const label = (r.floorLabel ?? "").trim() || "Model";
    let floor = bySpace.get(r.spaceId);
    if (!floor) {
      floor = { spaceId: r.spaceId, floorLabel: label, sortKey: floorSortKey(label), scans: [] };
      bySpace.set(r.spaceId, floor);
    }
    floor.scans.push({
      deliverableId: r.deliverableId,
      token: r.token,
      modelId: r.modelId,
      capturedAt: r.capturedAt,
      status: r.status,
      thumbnailUrl: r.thumbnailUrl ?? null,
    });
  }
  const floors = [...bySpace.values()];
  for (const f of floors) f.scans.sort(byCapturedAtAsc);
  floors.sort((a, b) => a.sortKey - b.sortKey || a.floorLabel.localeCompare(b.floorLabel));
  return floors;
}

/** The most recent (newest) scan on a floor, or null when the floor is empty. */
export function latestScan(floor: BuildingFloor): BuildingScan | null {
  return floor.scans.length ? floor.scans[floor.scans.length - 1] : null;
}

/** A floor with >=2 dated scans is a genuine time-series (scrubber/compare on). */
export function isTimeSeries(floor: BuildingFloor): boolean {
  return floor.scans.filter((s) => s.capturedAt).length >= 2;
}

/**
 * Pick a clamped before/after pair for compare mode. Indices are into the
 * chronological scan list; defaults to (first, last). Clamps out-of-range
 * indices and guarantees `before` is not newer than `after` by swapping.
 */
export function pickComparePair(
  scans: readonly BuildingScan[],
  aIndex?: number,
  bIndex?: number,
): { before: BuildingScan; after: BuildingScan } | null {
  if (scans.length === 0) return null;
  if (scans.length === 1) return { before: scans[0], after: scans[0] };
  const clamp = (i: number) => Math.max(0, Math.min(scans.length - 1, i));
  let a = clamp(aIndex ?? 0);
  let b = clamp(bIndex ?? scans.length - 1);
  if (a > b) [a, b] = [b, a];
  return { before: scans[a], after: scans[b] };
}

/** "Jun 5, 2024" from an ISO date (UTC, no TZ drift). Falls back to the raw
 *  string when unparseable, or "Undated" when null. */
export function formatCaptureDate(iso: string | null | undefined): string {
  if (!iso) return "Undated";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[parseInt(m[2], 10) - 1] ?? m[2];
  return `${month} ${parseInt(m[3], 10)}, ${m[1]}`;
}

/** "Aug 2023 – Sep 2025 · 20 scans" summary line for a floor. */
export function floorTimelineSummary(floor: BuildingFloor): string {
  const dated = floor.scans.filter((s) => s.capturedAt);
  const n = floor.scans.length;
  const noun = n === 1 ? "scan" : "scans";
  if (dated.length === 0) return `${n} ${noun}`;
  const first = dated[0].capturedAt as string;
  const last = dated[dated.length - 1].capturedAt as string;
  const span = (iso: string) => {
    const mm = /^(\d{4})-(\d{2})/.exec(iso);
    if (!mm) return iso;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[parseInt(mm[2], 10) - 1]} ${mm[1]}`;
  };
  return first === last
    ? `${span(first)} · ${n} ${noun}`
    : `${span(first)} – ${span(last)} · ${n} ${noun}`;
}

/** Building-level rollup: total scans, dated floors, time-series floors,
 *  overall capture date range. For the building hero/intro. */
export function buildingTimelineStats(floors: readonly BuildingFloor[]): {
  floorCount: number;
  scanCount: number;
  timeSeriesFloorCount: number;
  earliest: string | null;
  latest: string | null;
} {
  let scanCount = 0;
  let earliest: string | null = null;
  let latest: string | null = null;
  let tsCount = 0;
  for (const f of floors) {
    scanCount += f.scans.length;
    if (isTimeSeries(f)) tsCount += 1;
    for (const s of f.scans) {
      if (!s.capturedAt) continue;
      if (earliest === null || s.capturedAt < earliest) earliest = s.capturedAt;
      if (latest === null || s.capturedAt > latest) latest = s.capturedAt;
    }
  }
  return { floorCount: floors.length, scanCount, timeSeriesFloorCount: tsCount, earliest, latest };
}

/**
 * Public Matterport thumbnail URL for any model id — re-exported from
 * {@link matterportThumbnailUrl} in `matterport.ts`.
 */
export { matterportThumbnailUrl } from "@/lib/virtualspace/matterport";

/** The newest scan's thumbnail for a floor card; falls back to any scan that
 *  has one (newest-first), null when none do. */
export function latestThumbnail(floor: BuildingFloor): string | null {
  for (let i = floor.scans.length - 1; i >= 0; i--) {
    const t = floor.scans[i].thumbnailUrl;
    if (t) return t;
  }
  return null;
}

/** Count of a floor's scans that have a synced Matterport model (a real,
 *  openable interactive tour). */
export function readyScanCount(floor: BuildingFloor): number {
  return floor.scans.filter((s) => !!s.modelId).length;
}

/**
 * Nearest index (in the chronological scan list) of a scan that HAS a model id
 * (an openable tour), searching outward from `fromIndex`. Returns null when no
 * scan on the floor has a tour. Powers the "jump to a ready capture" action on
 * the tour-not-available fallback.
 */
export function nearestReadyScanIndex(
  scans: readonly BuildingScan[],
  fromIndex: number,
): number | null {
  if (scans.length === 0) return null;
  const start = Math.max(0, Math.min(scans.length - 1, fromIndex));
  if (scans[start]?.modelId) return start;
  for (let d = 1; d < scans.length; d++) {
    const lo = start - d;
    const hi = start + d;
    if (hi < scans.length && scans[hi]?.modelId) return hi; // prefer newer on ties
    if (lo >= 0 && scans[lo]?.modelId) return lo;
  }
  return null;
}

const DAY_MS = 86_400_000;

export type FloorFreshness = {
  /** recent <=30d · aging <=90d · stale >90d · unknown (no dated scan). */
  state: "recent" | "aging" | "stale" | "unknown";
  /** Whole days since the newest dated scan; null when unknown. */
  days: number | null;
  /** "12 days ago" / "3 months ago" / "Never scanned". */
  label: string;
};

/** Freshness of a floor from its newest dated scan vs `nowMs` (UTC-day math). */
export function floorFreshness(floor: BuildingFloor, nowMs: number): FloorFreshness {
  let newest: string | null = null;
  for (const s of floor.scans) {
    if (s.capturedAt && (newest === null || s.capturedAt > newest)) newest = s.capturedAt;
  }
  if (!newest) return { state: "unknown", days: null, label: "No date" };
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(newest);
  if (!m) return { state: "unknown", days: null, label: "No date" };
  const capMs = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  const days = Math.max(0, Math.floor((nowMs - capMs) / DAY_MS));
  const state = days <= 30 ? "recent" : days <= 90 ? "aging" : "stale";
  let label: string;
  if (days <= 0) label = "Today";
  else if (days === 1) label = "Yesterday";
  else if (days < 45) label = `${days} days ago`;
  else if (days < 365) label = `${Math.round(days / 30)} months ago`;
  else {
    const yrs = Math.floor(days / 365);
    label = `${yrs} ${yrs === 1 ? "year" : "years"} ago`;
  }
  return { state, days, label };
}

/**
 * Nearest scan index for a scrubber click/drag at `percent` (0..100) along
 * the same axis as {@link FloorScrubberMode}'s tick positions.
 */
export function nearestScanIndexAtPercent(
  tickPercents: readonly number[],
  percent: number,
): number {
  if (tickPercents.length === 0) return 0;
  const p = Number.isFinite(percent) ? percent : 0;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < tickPercents.length; i++) {
    const d = Math.abs((tickPercents[i] ?? 0) - p);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/**
 * Normalized x-positions (0..1, oldest->newest) of a floor's dated scans along
 * the building-wide time axis [rangeStartMs, rangeEndMs]. The renderer draws a
 * tick per position for a cadence sparkline (clustered vs spread = cadence;
 * rightmost = recency). Falls back to even spacing when the floor has a single
 * date or the range is degenerate.
 */
export function scanTickPositions(
  floor: BuildingFloor,
  rangeStartMs: number,
  rangeEndMs: number,
): number[] {
  const dated = floor.scans
    .map((s) => s.capturedAt)
    .filter((x): x is string => !!x)
    .map((iso) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
      return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
    })
    .filter((x): x is number => x !== null)
    .sort((a, b) => a - b);
  if (dated.length === 0) return [];
  const span = rangeEndMs - rangeStartMs;
  if (span <= 0) {
    // Degenerate range — space evenly so the sparkline still reads.
    return dated.map((_, i) => (dated.length === 1 ? 1 : i / (dated.length - 1)));
  }
  return dated.map((t) => Math.max(0, Math.min(1, (t - rangeStartMs) / span)));
}

/** Parse an ISO date to its UTC ms (for sparkline range math); null on miss. */
export function isoToUtcMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
}

/**
 * True when a capture date is strictly AFTER today (UTC day) — a scheduled /
 * not-yet-captured scan. These come from the import's "Scan Event Date" (which
 * can hold planned future visits) and must be HIDDEN from the timeline: they
 * aren't real captures (no Matterport model exists yet), so showing them as
 * "processing" is wrong. Undated (null) is NOT future — kept. Today is kept.
 */
export function isFutureCaptureDate(
  iso: string | null | undefined,
  nowMs: number,
): boolean {
  const cap = isoToUtcMs(iso);
  if (cap === null) return false;
  const d = new Date(nowMs);
  const todayUtc = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return cap > todayUtc;
}

/**
 * Airtable import states that mean "no Matterport model exists / will exist"
 * — placeholders that must NOT render as "Processing" on SpaceLapse.
 * Case-insensitive substring match on `provider_state`.
 */
export function isNonCaptureProviderState(
  status: string | null | undefined,
): boolean {
  if (!status) return false;
  return /await|cancel|upload reminder/i.test(status);
}

/**
 * Customer-facing badge when a scan has no openable model. Never says
 * "Processing" for cancelled / awaiting placeholders (founder 2026-07-09).
 */
export function scanUnavailableLabel(
  status: string | null | undefined,
): string {
  if (!status) return "Unavailable";
  if (/cancel/i.test(status)) return "Cancelled";
  if (/await|upload reminder/i.test(status)) return "Scheduled";
  // Customer Matterport ownership — live tour, not offline.
  // Must run before any "archiv" substring checks (none today, but
  // keep transferred out of the archived vocabulary).
  if (/transfer/i.test(status)) return "Customer-hosted";
  if (/archiv|inactive/i.test(status)) return "Archived";
  if (/repair|process|upload/i.test(status)) return "Processing";
  return "Unavailable";
}

/** Drop scheduled/not-yet-captured (future-dated) scan rows. Floors left with
 *  zero rows naturally vanish (groupFloors builds only from the rows it gets),
 *  which correctly hides a floor that has no real captures yet. */
export function dropFutureScanRows(
  rows: readonly BuildingScanRow[],
  nowMs: number,
): BuildingScanRow[] {
  return rows.filter((r) => !isFutureCaptureDate(r.capturedAt, nowMs));
}

/**
 * Timeline hygiene: drop future-dated rows AND import placeholders that have
 * no Matterport model (Awaiting Scan / Cancelled / Upload Reminder). Pure —
 * no DB writes, no notifications. Shared/Archived rows without a model stay
 * (rare; badge via {@link scanUnavailableLabel}).
 */
export function dropNonTimelineScanRows(
  rows: readonly BuildingScanRow[],
  nowMs: number,
): BuildingScanRow[] {
  return dropFutureScanRows(rows, nowMs).filter((r) => {
    if (r.modelId) return true;
    return !isNonCaptureProviderState(r.status);
  });
}

/** Case-insensitive filter of floors by label substring. Blank query -> all. */
export function filterFloors(
  floors: readonly BuildingFloor[],
  query: string,
): BuildingFloor[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...floors];
  return floors.filter((f) => f.floorLabel.toLowerCase().includes(q));
}

export type FloorSortMode = "floor" | "recent";

/** Sort floors by building order (`floor`, the default) or by most-recently
 *  scanned (`recent`, newest first; undated floors sink). Pure (returns a copy). */
export function sortFloors(
  floors: readonly BuildingFloor[],
  mode: FloorSortMode,
): BuildingFloor[] {
  const copy = [...floors];
  if (mode === "recent") {
    const newestMs = (f: BuildingFloor): number => {
      let n: number | null = null;
      for (const s of f.scans) {
        const ms = isoToUtcMs(s.capturedAt);
        if (ms !== null && (n === null || ms > n)) n = ms;
      }
      return n ?? -Infinity;
    };
    copy.sort((a, b) => newestMs(b) - newestMs(a) || a.sortKey - b.sortKey);
    return copy;
  }
  copy.sort((a, b) => a.sortKey - b.sortKey || a.floorLabel.localeCompare(b.floorLabel));
  return copy;
}
